import { getConsumptionAPI } from './httpUtils';

export interface CopilotAccessEntry {
  hasAccess: boolean;
  enableTileForm: boolean;
  enableSelfHelp: boolean;
  handoffConfigured?: boolean;
  handoffSettingsUnavailable?: boolean;
  handoff?: unknown;
}

export type CopilotAccessMap = Record<string, CopilotAccessEntry>;

export function resolveTileHandoffKey(
  map: CopilotAccessMap, tileName: string, serviceName?: string
): string {
  const tileKey = tileName.trim().toLowerCase();
  const internalEntry = map[tileKey];
  if (internalEntry && (!internalEntry.hasAccess
      || internalEntry.handoffConfigured === true || internalEntry.handoff != null)) return tileKey;
  const serviceKey = serviceName?.trim().toLowerCase() || '';
  const entry = map[serviceKey];
  return entry?.hasAccess === true && (entry.handoffConfigured === true || entry.handoff != null)
    ? serviceKey : tileKey;
}

export function parseCopilotAccessMap(payload: unknown): CopilotAccessMap {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload))
    throw new Error('Invalid copilot access-map response.');
  const keys = new Set<string>();
  return Object.fromEntries(Object.entries(payload).map(([key, value]) => {
    const normalized = key.trim().toLowerCase();
    if (!normalized || keys.has(normalized)) throw new Error('Invalid or duplicate access-map key.');
    keys.add(normalized);
    if (typeof value === 'boolean')
      return [normalized, { hasAccess: value, enableTileForm: true, enableSelfHelp: false }];
    if (typeof value !== 'object' || value === null || Array.isArray(value)
        || !('hasAccess' in value) || typeof value.hasAccess !== 'boolean')
      throw new Error('Invalid copilot access decision.');
    const enableTileForm = 'enableTileForm' in value ? value.enableTileForm ?? true : true;
    const enableSelfHelp = 'enableSelfHelp' in value ? value.enableSelfHelp ?? false : false;
    if (typeof enableTileForm !== 'boolean' || typeof enableSelfHelp !== 'boolean')
      throw new Error('Invalid copilot access flags.');
    const handoffConfigured = 'handoffConfigured' in value ? value.handoffConfigured : undefined;
    if (handoffConfigured !== undefined && typeof handoffConfigured !== 'boolean')
      throw new Error('Invalid copilot handoff flag.');
    const handoffSettingsUnavailable = 'handoffSettingsUnavailable' in value ? value.handoffSettingsUnavailable : undefined;
    if (handoffSettingsUnavailable !== undefined && typeof handoffSettingsUnavailable !== 'boolean')
      throw new Error('Invalid handoff settings availability.');
    return [normalized, {
      hasAccess: value.hasAccess, enableTileForm, enableSelfHelp,
      ...(handoffConfigured !== undefined && { handoffConfigured }),
      ...(handoffSettingsUnavailable !== undefined && { handoffSettingsUnavailable }),
      ...('handoff' in value && { handoff: value.handoff })
    }];
  }));
}

function applyHandoffSettings(map: CopilotAccessMap, payload: unknown): CopilotAccessMap {
  const welcomeKey = 'McapsCopilotTileHandoffWelcomeMessage'.toLowerCase();
  const configKey = 'McapsCopilotTileHandoffConfig'.toLowerCase();
  if (!Array.isArray(payload)) throw new Error('Invalid public-settings response.');
  const settings = new Map<string, string>();
  for (const item of payload) {
    if (typeof item !== 'object' || item === null || typeof item.key !== 'string')
      throw new Error('Invalid public-settings entry.');
    const key = item.key.trim().toLowerCase();
    if (key !== welcomeKey && key !== configKey) continue;
    if (settings.has(key) || typeof item.value !== 'string')
      throw new Error('Invalid or duplicate public handoff setting.');
    settings.set(key, item.value);
  }
  const configValue = settings.get(configKey);
  const configured: unknown = configValue === undefined ? {} : JSON.parse(configValue);
  if (typeof configured !== 'object' || configured === null || Array.isArray(configured))
    throw new Error('Invalid tile handoff settings.');
  const rows = new Map<string, unknown>();
  for (const [key, row] of Object.entries(configured)) {
    const normalized = key.trim().toLowerCase();
    if (!normalized || rows.has(normalized)) throw new Error('Invalid or duplicate handoff tile key.');
    rows.set(normalized, row);
  }
  let welcomeMessages: unknown;
  const welcomeValue = settings.get(welcomeKey);
  if (welcomeValue !== undefined) {
    try {
      welcomeMessages = JSON.parse(welcomeValue);
    } catch (error) {
      if (!(error instanceof SyntaxError)) throw error;
      // Only configured handoffs need this template; their parser will fail closed.
      console.error('Tile handoff welcome setting contains invalid JSON.');
    }
  }
  return Object.fromEntries(Object.entries(map).map(([key, entry]) => {
    const { hasAccess, enableTileForm, enableSelfHelp } = entry;
    const row = rows.get(key);
    const handoff = !rows.has(key) || !hasAccess ? null
      : typeof row !== 'object' || row === null || Array.isArray(row)
        ? { error: 'ConfigurationUnavailable' }
        : {
          accessPointKey: key,
          isEnabled: 'enabled' in row ? row.enabled : undefined,
          serviceName: 'serviceName' in row ? row.serviceName : undefined,
          agentName: 'agentName' in row ? row.agentName : undefined,
          portalName: 'portalName' in row ? row.portalName : undefined,
          portalUrl: 'portalUrl' in row ? row.portalUrl : undefined,
          welcomeMessages
        };
    // Authorization stays API-owned; SQL presentation is deliberately not a fallback.
    return [key, { hasAccess, enableTileForm, enableSelfHelp, handoffConfigured: rows.has(key), handoff }];
  }));
}

export async function loadCopilotAccessMap(
  authClient: Parameters<typeof getConsumptionAPI>[1], upn: string
): Promise<CopilotAccessMap> {
  if (!upn.trim()) throw new Error('A signed-in user is required to load copilot access.');
  const [response, settings] = await Promise.all([
    getConsumptionAPI(`/api/V2_User/GetCopilotAccessMap?upn=${encodeURIComponent(upn)}`, authClient),
    getConsumptionAPI('/api/User/GetEnvironmentVariables', authClient).catch(() => {
      console.error('Public handoff settings request failed.');
      return null;
    })
  ]);
  if (response?.status !== 200) throw new Error('Copilot access configuration request failed.');
  const map = parseCopilotAccessMap(response.data);
  try {
    if (settings?.status !== 200) throw new Error('Public handoff settings request failed.');
    return applyHandoffSettings(map, settings.data);
  } catch {
    console.error('Tile handoff presentation is unavailable; API access decisions were retained.');
    return Object.fromEntries(Object.entries(map).map(([key, { hasAccess, enableTileForm, enableSelfHelp }]) =>
      [key, { hasAccess, enableTileForm, enableSelfHelp,
        handoffConfigured: false, handoff: null, handoffSettingsUnavailable: true }]));
  }
}
// Generated by Copilot | 2026-10-26 | 45 lines added/updated/removed | Auto | 1322441 shared existing access-map loader with typed normalization | Time Taken 6 min
// Generated by Copilot | 2026-10-27 | 6 lines added/updated/removed | Auto | 1322441 validate and preserve the optional handoff scope flag | Time Taken 3 min
// Generated by Copilot | 2026-10-27 | 11 lines added | Auto | 1322441 resolve explicit service-name handoff mappings while preserving exact-key decisions | Time Taken 3 min
// Generated by Copilot | 2026-10-27 | 8 lines added/updated/removed | Auto | 1322441 prefer a granted service handoff over a granted legacy row without overriding denials or internal handoffs | Time Taken 3 min
// Generated by Copilot | 2026-10-28 | 65 lines added/updated/removed | Auto | 1323031 combine fresh public presentation settings with unchanged API access decisions | Time Taken 15 min
// Generated by Copilot | 2026-10-29 | 23 lines added/updated/removed | Auto | Preserve authoritative access flags and explicitly mark unavailable handoff presentation | Time Taken 6 min
